import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logoamerisa from '../assets/logoamerisazul.png';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 8, // Smaller font to fit in half page
    padding: 10,
    position: 'relative',
  },
  voucherContainer: {
    border: '1pt dashed black',
    padding: 10,
    marginBottom: 10, // Space between the two vouchers
    height: '48%', // Take up roughly half of the page (leaving space for margin)
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 5,
  },
  headerText: {
    flex: 1,
    textAlign: 'center',
    marginLeft: 5,
    fontSize: 7,
  },
  content: {
    marginTop: 10,
  },
  section: {
    marginBottom: 10,
  },
  title: {
    fontSize: 12,
    marginBottom: 10,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  text: {
    marginBottom: 3,
    textAlign: 'justify',
    fontSize: 8,
  },
  bold: {
    fontWeight: 'bold',
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  table: {
    display: 'flex',
    width: 'auto',
    border: '1pt solid black',
    marginBottom: 5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1pt solid black',
  },
  tableColHeader: {
    borderRight: '1pt solid black',
    padding: 3,
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  tableCol: {
    borderRight: '1pt solid black',
    padding: 3,
  },
  totalRow: {
    flexDirection: 'row',
    borderTop: '1pt solid black',
    backgroundColor: '#f0f0f0',
  },
  authorizeSignature: {
    marginTop: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signature: {
    width: '45%',
    borderTop: '1pt solid black',
    paddingTop: 3,
    textAlign: 'center',
    fontSize: 7,
  },
  logo: {
    height: 30,
    width: 60,
  },
  underline: {
    textDecoration: 'underline',
  },
  pageFooter: {
    fontSize: 6,
    textAlign: 'center',
    marginTop: 5,
  },
  cutHere: {
    fontSize: 7,
    textAlign: 'center',
    marginVertical: 5,
    fontStyle: 'italic',
    color: '#555555',
  },
});

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Extraer los componentes de la fecha (año, mes, día) directamente
  const [year, month, day] = dateString.split('T')[0].split('-').map(num => parseInt(num, 10));
  
  // Crear la fecha con los componentes exactos para evitar ajustes de zona horaria
  const date = new Date(year, month - 1, day);
  
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const formatConcept = (concept) => {
  const labels = {
    'diesel': 'Diesel',
    'gasolina_magna': 'Gasolina Magna',
    'gasolina_premium': 'Gasolina Premium',
    'aditivos': 'Aditivos',
    'aceites': 'Aceites'
  };
  return labels[concept] || concept;
};

// Single voucher component to be reused
const ValeGasolina = ({ vale }) => {
  // Safely handle undefined values with explicit fallbacks
  const origen = vale.origen || 'No especificado';
  const destino = vale.destino || 'No especificado';
  const ruta = `${origen} - ${destino}`;
  const proveedor = vale.proveedor || 'No especificado';
  const comentarios = vale.comentarios || 'N/A';
  const driverName = vale.driver && vale.driver !== 'undefined undefined' ? vale.driver : '________________';
  const vehiclePlate = vale.vehicle || 'N/A';
  const fecha = formatDate(vale.fecha);
  const tipo = formatConcept(vale.tipo || 'diesel');
  const cantidad = vale.cantidad || 0;
  const precio = vale.precio || 0;
  const total = parseFloat(vale.total) || parseFloat(cantidad) * parseFloat(precio) || 0;

  return (
    <View style={styles.voucherContainer}>
      <View style={styles.header}>
        <Image src={logoamerisa} style={styles.logo} />
        <Text style={styles.headerText}>
          AL TRANSPORTES PENINSULARES{"\n"}
          82-1 centro MERIDA PROGRESO 97320, YUCATAN{"\n"}
          TPE120221Z0
        </Text>
      </View>

      <View style={styles.content}>
        <Text style={styles.title}>VALE DE GASOLINA</Text>
        
        <View style={styles.grid}>
          <Text style={styles.text}><Text style={styles.bold}>N° de Folio:</Text> <Text style={styles.underline}>{vale.folio || 'N/A'}</Text></Text>
          <Text style={styles.text}><Text style={styles.bold}>Fecha:</Text> <Text style={styles.underline}>{fecha}</Text></Text>
        </View>
        
        <View style={styles.section}>
          <Text style={styles.text}><Text style={styles.bold}>Embarque Ref:</Text> <Text style={styles.underline}>{vale.shipment || 'N/A'}</Text></Text>
          <Text style={styles.text}><Text style={styles.bold}>Ruta:</Text> <Text style={styles.underline}>{ruta}</Text></Text>
          <Text style={styles.text}><Text style={styles.bold}>Conductor:</Text> <Text style={styles.underline}>{driverName}</Text></Text>
          <Text style={styles.text}><Text style={styles.bold}>Vehículo:</Text> <Text style={styles.underline}>{vehiclePlate}</Text></Text>
          <Text style={styles.text}><Text style={styles.bold}>Estación de Gasolina:</Text> <Text style={styles.underline}>{proveedor}</Text></Text>
        </View>
        
        <View style={styles.section}>
          <Text style={[styles.bold, {marginBottom: 3}]}>Detalle del Vale:</Text>
          
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={{...styles.tableColHeader, width: '25%'}}>
                <Text>Producto</Text>
              </View>
              <View style={{...styles.tableColHeader, width: '25%'}}>
                <Text>Cantidad (L)</Text>
              </View>
              <View style={{...styles.tableColHeader, width: '25%'}}>
                <Text>Precio/L</Text>
              </View>
              <View style={{...styles.tableColHeader, width: '25%', borderRight: 'none'}}>
                <Text>Total ($)</Text>
              </View>
            </View>
            
            <View style={styles.tableRow}>
              <View style={{...styles.tableCol, width: '25%'}}>
                <Text>{tipo}</Text>
              </View>
              <View style={{...styles.tableCol, width: '25%'}}>
                <Text>{cantidad}</Text>
              </View>
              <View style={{...styles.tableCol, width: '25%'}}>
                <Text>${precio}</Text>
              </View>
              <View style={{...styles.tableCol, width: '25%', borderRight: 'none'}}>
                <Text>${total.toFixed(2)}</Text>
              </View>
            </View>
            
            <View style={styles.totalRow}>
              <View style={{...styles.tableCol, width: '75%', textAlign: 'right'}}>
                <Text style={styles.bold}>TOTAL:</Text>
              </View>
              <View style={{...styles.tableCol, width: '25%', borderRight: 'none'}}>
                <Text style={styles.bold}>${total.toFixed(2)}</Text>
              </View>
            </View>
          </View>
          
          {comentarios !== 'N/A' && (
            <View style={styles.section}>
              <Text style={styles.bold}>Observaciones:</Text>
              <Text>{comentarios}</Text>
            </View>
          )}
        </View>
        
        <View style={styles.authorizeSignature}>
          <View style={styles.signature}>
            <Text>Autorizado por</Text>
          </View>
          <View style={styles.signature}>
            <Text>Recibido por (Conductor)</Text>
          </View>
        </View>
        
        <Text style={styles.pageFooter}>
          Este documento es comprobante oficial para el suministro de combustible.
          Conservar para fines administrativos.
        </Text>
      </View>
    </View>
  );
};

// Main document with two identical vouchers per page
const GasolinaDocument = ({ vale }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ValeGasolina vale={vale} />
        <Text style={styles.cutHere}>- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -</Text>
        <ValeGasolina vale={vale} />
      </Page>
    </Document>
  );
};

export default GasolinaDocument;